import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTranslation } from 'react-i18next';
import { STRIPE_CONFIG } from '../../config/stripe';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

/**
 * PayPal delta payment button for manage flows (edit, extend, pay-now).
 *
 * Flow:
 * 1. User clicks → create-paypal-delta-order (gets orderId + approvalUrl)
 * 2. Opens PayPal approval in popup window
 * 3. PayPal redirects back → capture-paypal-delta-order
 * 4. Calls onSuccess with captureId
 */
export default function PayPalDeltaButton({ token, amount, description, onSuccess, onError, triggerNotificationOnCapture = false }) {
  const theme = useTheme();
  const { formatCurrency } = useCurrency();
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const orderIdRef = useRef(null);
  // Refs mirror state for use inside long-lived closures (the popup-close poll),
  // which would otherwise capture stale values from the render that created them.
  const awaitingApprovalRef = useRef(false);
  const capturedRef = useRef(false);
  const cancelledRef = useRef(false);

  const setAwaiting = (val) => {
    awaitingApprovalRef.current = val;
    setAwaitingApproval(val);
  };

  // Clean up popup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
    };
  }, []);

  // Listen for message from the popup's return bridge (public/paypal-return.html).
  useEffect(() => {
    const handleMessage = async (event) => {
      // Only trust messages from our own origin (the bridge page).
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'paypal-delta-approved' && event.data?.orderId) {
        if (capturedRef.current) return;
        setAwaiting(false);
        if (pollRef.current) clearInterval(pollRef.current);
        await captureOrder(event.data.orderId);
      } else if (event.data?.type === 'paypal-delta-cancelled') {
        cancelledRef.current = true;
        setAwaiting(false);
        if (pollRef.current) clearInterval(pollRef.current);
        if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [token]);

  const captureOrder = async (orderId) => {
    if (capturedRef.current) return;
    capturedRef.current = true;
    setProcessing(true);
    try {
      const res = await fetch(`${API_BASE}/manage/capture-paypal-delta-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orderId, triggerNotification: triggerNotificationOnCapture })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to capture PayPal payment');
      }

      const data = await res.json();
      onSuccess(data.captureId);
    } catch (err) {
      // Capture failed — allow a retry (e.g. user closed before approving).
      capturedRef.current = false;
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleClick = async () => {
    setProcessing(true);
    // Reset per-attempt state so a retry after a previous cancel/decline works.
    capturedRef.current = false;
    cancelledRef.current = false;
    orderIdRef.current = null;
    try {
      // 1. Create PayPal order
      const res = await fetch(`${API_BASE}/manage/create-paypal-delta-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount, description, origin: window.location.origin })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create PayPal order');
      }

      const { orderId, approvalUrl } = await res.json();
      orderIdRef.current = orderId;

      if (!approvalUrl) {
        throw new Error('PayPal approval URL not available');
      }

      // 2. Open PayPal in popup
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        approvalUrl,
        'paypal-delta-payment',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
      );
      popupRef.current = popup;
      setAwaiting(true);
      setProcessing(false);

      // 3. Poll for popup close. Primary completion is the postMessage from the
      //    return bridge; this is a fallback for when the message never arrives
      //    (e.g. popup blocked from posting). Uses refs, not state, so it sees
      //    live values rather than the (stale) closure snapshot.
      pollRef.current = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(pollRef.current);
          // Only attempt a fallback capture if the user didn't explicitly cancel
          // and we haven't already captured via the postMessage path.
          if (awaitingApprovalRef.current && !capturedRef.current && !cancelledRef.current && orderIdRef.current) {
            captureOrder(orderIdRef.current);
          }
          setAwaiting(false);
        }
      }, 1000);

    } catch (err) {
      onError(err.message);
      setProcessing(false);
    }
  };

  if (awaitingApproval) {
    return (
      <div className="text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <svg className="w-5 h-5 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {t('manage.awaitingPayPal', 'Complete your payment in the PayPal window...')}
        </div>
        <button
          onClick={() => {
            if (popupRef.current && !popupRef.current.closed) {
              popupRef.current.focus();
            }
          }}
          className="text-sm underline"
          style={{ color: theme.colors.primary.base }}
        >
          {t('manage.focusPayPal', 'Click here if the PayPal window is hidden')}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={processing}
      className="w-full py-4 rounded-xl font-semibold text-white transition-colors disabled:opacity-50"
      style={{ backgroundColor: '#0070BA' }}
    >
      {processing
        ? t('manage.processing', 'Processing...')
        : `${t('manage.payWithPayPal', 'Pay with PayPal')} ${formatCurrency(amount)}`}
    </button>
  );
}
